import { Key, useEffect, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Exercise } from '@entities/exercise'
import { ExerciseToAddCard } from '@pages/TrainingConstructor/ui/exerciseCard'
import { observer } from 'mobx-react-lite'
import { useExercisesStore } from '@entities/exercise/model/dto.provider'
import { useExerciseFilterStore } from '@entities/exercise/model/filters.provider'

const ExerciseSkeletonCard = () => (
	<div className="training-exercise-skeleton-card" aria-hidden="true">
		<div className="training-exercise-skeleton-image" />
		<div className="training-exercise-skeleton-copy">
			<div className="training-exercise-skeleton-line is-title" />
			<div className="training-exercise-skeleton-line is-primary" />
			<div className="training-exercise-skeleton-line is-secondary" />
		</div>
		<div className="training-exercise-skeleton-action" />
	</div>
)

const ExerciseListSkeleton = ({ amount }: { amount: number }) => (
	<div className="training-exercise-skeleton-list" aria-hidden="true">
		{Array.from({ length: amount }, (_, index) => (
			<ExerciseSkeletonCard key={index} />
		))}
	</div>
)

export const CreateTraining = observer(() => {
	const queryClient = useQueryClient()
	const exercisesStore = useExercisesStore()
	const filterStore = useExerciseFilterStore()
	const { getFtsSearchTerm } = filterStore
	const { exercises, getExercises, isLoadingExercises } = exercisesStore

	const containerRef = useRef<HTMLDivElement>(null)
	const scrollFrameRef = useRef<number | null>(null)

	useEffect(() => {
		getExercises(queryClient)

		const container = containerRef.current
		if (!container) return

		const checkScrollPosition = () => {
			scrollFrameRef.current = null

			const scrollTop = container.scrollTop
			const scrollHeight = container.scrollHeight
			const clientHeight = container.clientHeight

			const isNearBottom = scrollHeight - (scrollTop + clientHeight) < 900
			if (isNearBottom) {
				getExercises(queryClient)
			}
		}

		const handleScroll = () => {
			if (scrollFrameRef.current !== null) return

			scrollFrameRef.current = window.requestAnimationFrame(checkScrollPosition)
		}

		container.addEventListener('scroll', handleScroll, { passive: true })

		return () => {
			container.removeEventListener('scroll', handleScroll)
			if (scrollFrameRef.current !== null) {
				window.cancelAnimationFrame(scrollFrameRef.current)
			}
		}
	}, [getExercises, queryClient])

	return (
		<div ref={containerRef} className="training-exercise-list-scroll">
			{isLoadingExercises && exercises.length === 0 && (
				<ExerciseListSkeleton amount={6} />
			)}
			{!isLoadingExercises &&
				exercises.length === 0 &&
				getFtsSearchTerm() !== '' && (
					<div className="training-empty-state">
						Ничего не найдено. Попробуйте изменить запрос или фильтры.
					</div>
				)}
			{exercises.map((exercise: Exercise, index: Key | null | undefined) => (
				<ExerciseToAddCard
					key={exercise.id_exercise || index}
					exercise={exercise}
				/>
			))}
			{isLoadingExercises && exercises.length > 0 && (
				<ExerciseListSkeleton amount={2} />
			)}
		</div>
	)
})
